/* eslint-disable dot-notation */
import { TWidget } from 'widgets/Widget';

const SERVICE_CHANGE_EVENT = 'consentServiceChange';
const SERVICE_KLAVIYO = 'Klaviyo';

type TAddToCartData = {
    dataObj?: object;
    eventName?: string;
};

/**
 * @param Widget Base widget for extending
 */
function KlaviyoEventListenerClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @class KlaviyoEventListener
     * @augments Widget
     * @classdesc KlaviyoEventListener Widget - used to
     */
    class KlaviyoEventListener extends Widget {
        isConsentGiven: boolean = false;

        isInstanceInitialized: boolean = false;

        prefs() {
            return {
                action: '',
                dataObj: null,
                ...super.prefs()
            };
        }

        init() {
            super.init();
            this.eventBus().on(SERVICE_CHANGE_EVENT, 'processServiceChange');
            this.initInstanceWithDelay();
        }

        /**
         * @description Handles changes to service consent, specifically for Klaviyo services.
         * @param type - The type of service for which the consent change is being processed.
         * @param accepted - A boolean indicating whether the consent was accepted or declined.
         */
        processServiceChange(type: string, accepted: boolean) {
            if (type === SERVICE_KLAVIYO) {
                this.isConsentGiven = accepted;
                this.initInstanceWithDelay();
            }
        }

        /**
         * @description Initializes the Klaviyo event tracking instance if consent has been given.
         * This method init of sends  tracking event to Klaviyo with delay till page fully loaded.
         */
        initInstanceWithDelay() {
            if (document.readyState !== 'complete') {
                document.addEventListener('readystatechange', event => {
                    if ((event.target as Document).readyState === 'complete') {
                        setTimeout(() => this.initInstance(), 0);
                    }
                });
            } else {
                this.initInstance();
            }
        }

        /**
         * @description Initializes the Klaviyo event tracking instance if consent has been given.
         * This method sends a single tracking event to Klaviyo based on the widget's preferences.
         */
        initInstance() {
            if (!this.isConsentGiven || this.isInstanceInitialized) {
                return;
            }

            const action = this.prefs().action;
            const dataObj = this.prefs().dataObj;

            if (!window['klaviyo'] || !action || !dataObj) {
                return;
            }

            window['klaviyo'].push(['track', action, dataObj]);
            this.eventBus().on('ESP.product.added.to.cart', 'productAddedToCart');

            this.isInstanceInitialized = true;
        }

        /**
         * @description Handles the 'ESP.product.added.to.cart' event by sending a tracking event to Klaviyo.
         * @param TAddToCartData addToCartData - The data object containing the event name and payload for the Klaviyo tracking event.
         */
        productAddedToCart(addToCartData: TAddToCartData) {
            if (addToCartData) {
                window['klaviyo'].push(['track', addToCartData.eventName, addToCartData.dataObj]);
            }
        }
    }

    return KlaviyoEventListener;
}

export type TKlaviyoEventListener = ReturnType<typeof KlaviyoEventListenerClassCreator>;

export type TKlaviyoEventListenerInstance = InstanceType<TKlaviyoEventListener>;

export default KlaviyoEventListenerClassCreator;
